import { usePageTitle } from '../hooks/usePageTitle';
import React, { useEffect, useState } from 'react';
import api from '../api';
import { useTheme } from '../contexts/ThemeContext';

interface Kisi {
  id: string;
  ad: string;
  soyad: string;
  email: string;
  izinler?: Record<string, boolean>;
  tip: 'ogrenci' | 'egitmen';
}

const IZIN_ETIKETLER: { key: string; label: string }[] = [
  { key: 'etkinlik_qr', label: 'Etkinlik QR Girişi' },
];

const TIP_LABEL: Record<string, string> = { ogrenci: 'Öğrenci', egitmen: 'Eğitmen' };

export default function Personel() {
  usePageTitle('Personel');
  const { isDark } = useTheme();

  const card = isDark ? '#1a1a1a' : 'white';
  const border = isDark ? '#2a2a2a' : '#eee';
  const textPrimary = isDark ? '#e8e8e8' : '#1a1a1a';
  const textSec = isDark ? '#999' : '#888';
  const textMuted = isDark ? '#666' : '#aaa';

  const [kisiler, setKisiler] = useState<Kisi[]>([]);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [arama, setArama] = useState('');

  async function yukle() {
    setYukleniyor(true);
    const [oRes, eRes] = await Promise.all([
      api.get('/api/ogrenciler?tum=true').catch(() => ({ data: [] })),
      api.get('/api/egitmenler').catch(() => ({ data: [] })),
    ]);
    const ogrenciler: Kisi[] = (oRes.data || []).map((o: any) => ({ ...o, tip: 'ogrenci' as const }));
    const egitmenler: Kisi[] = (eRes.data || []).map((e: any) => ({ ...e, tip: 'egitmen' as const }));
    setKisiler([...ogrenciler, ...egitmenler]);
    setYukleniyor(false);
  }

  useEffect(() => { yukle(); }, []);

  async function izinToggle(kisi: Kisi, key: string) {
    const yeniIzinler = { ...kisi.izinler, [key]: !kisi.izinler?.[key] };
    setKisiler(prev => prev.map(k => k.id === kisi.id && k.tip === kisi.tip ? { ...k, izinler: yeniIzinler } : k));
    const yol = kisi.tip === 'ogrenci' ? `/api/ogrenciler/${kisi.id}/izinler` : `/api/egitmenler/${kisi.id}/izinler`;
    await api.patch(yol, { izinler: yeniIzinler });
  }

  const hasAnyIzin = (k: Kisi) => !!k.izinler && Object.values(k.izinler).some(Boolean);
  const rozetliler = kisiler.filter(hasAnyIzin);
  const aramaSonuclari = arama.length >= 2
    ? kisiler.filter(k => `${k.ad} ${k.soyad} ${k.email}`.toLowerCase().includes(arama.toLowerCase())).slice(0, 12)
    : [];

  const inp: React.CSSProperties = { width: '100%', maxWidth: 360, padding: '9px 12px', border: `1px solid ${border}`, borderRadius: 8, fontSize: 14, boxSizing: 'border-box', outline: 'none', background: isDark ? '#111' : 'white', color: textPrimary };

  function KisiKart({ k }: { k: Kisi }) {
    return (
      <div style={{ background: card, border: `1px solid ${border}`, borderRadius: 10, padding: '14px 16px', marginBottom: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <div>
            <span style={{ fontSize: 14, fontWeight: 700, color: textPrimary }}>{k.ad} {k.soyad}</span>
            <span style={{ marginLeft: 8, fontSize: 11, padding: '2px 8px', borderRadius: 10, background: isDark ? '#222' : '#f0f0f0', color: textSec }}>
              {TIP_LABEL[k.tip]}
            </span>
          </div>
          <span style={{ fontSize: 12, color: textSec }}>{k.email}</span>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {IZIN_ETIKETLER.map(({ key, label }) => {
            const aktif = !!k.izinler?.[key];
            return (
              <button key={key} onClick={() => izinToggle(k, key)} style={{
                padding: '3px 10px', borderRadius: 20, fontSize: 11, cursor: 'pointer',
                border: `1px solid ${aktif ? '#534AB7' : (isDark ? '#3a3a3a' : '#ddd')}`,
                background: aktif ? (isDark ? 'rgba(83,74,183,0.2)' : '#EEEDFE') : card,
                color: aktif ? '#3C3489' : textSec,
                fontWeight: aktif ? 600 : 400,
              }}>
                {aktif ? '✓ ' : ''}{label}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: 32, maxWidth: 900 }}>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: textPrimary }}>Personel</h2>
        <p style={{ margin: '4px 0 0', color: textSec, fontSize: 13 }}>
          "Personel" ayrı bir profil değil — mevcut bir öğrenci veya eğitmen hesabına eklenen bir yetki rozetidir (örn. etkinlik kapı girişinde QR okuma). Profilleri değişmez, sadece bu yetkiyi kazanırlar.
        </p>
      </div>

      <div style={{ marginBottom: 28 }}>
        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: textSec, marginBottom: 6 }}>Öğrenci veya eğitmen ara</label>
        <input style={inp} value={arama} onChange={e => setArama(e.target.value)} placeholder="Ad, soyad veya e-posta yazın (en az 2 karakter)…" />
        {yukleniyor ? (
          <p style={{ color: textMuted, marginTop: 10 }}>Yükleniyor…</p>
        ) : arama.length >= 2 ? (
          <div style={{ marginTop: 12 }}>
            {aramaSonuclari.length === 0 ? (
              <p style={{ color: textMuted, fontSize: 13 }}>Sonuç bulunamadı</p>
            ) : (
              aramaSonuclari.map(k => <KisiKart key={`${k.tip}-${k.id}`} k={k} />)
            )}
          </div>
        ) : null}
      </div>

      <div>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: textPrimary, marginBottom: 12 }}>
          Personel Rozeti Olanlar {rozetliler.length > 0 && `(${rozetliler.length})`}
        </h3>
        {rozetliler.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: textMuted, background: card, borderRadius: 10, border: `1px solid ${border}` }}>
            Henüz kimseye personel yetkisi verilmedi
          </div>
        ) : (
          rozetliler.map(k => <KisiKart key={`${k.tip}-${k.id}`} k={k} />)
        )}
      </div>
    </div>
  );
}
